import React from 'react';
import ColumnDragHandle from '../components/ColumnDragHandle';
import ColumnResizeHandle from '../components/ColumnResizeHandle';

export function applyResizeToGrid(
  gridData: any[] | null | undefined,
  widths: { column: string; width: number }[],
  colTypes: string[] | undefined,
  onColumnResize: ((column: string, width: number) => void) | undefined
): any[] | null | undefined {
  if (!gridData) {
    return gridData;
  }

  const widthMap = widths.length > 0
    ? new Map(widths.map(({ column, width }) => [column, width]))
    : new Map<string, number>();

  return gridData.map((row, rowIndex) => {
    if (rowIndex === 0) {
      return row;
    }

    const isFirstDataRow = rowIndex === 1;

    return row.map((cell: any, colIndex: number) => {
      const key = cell.columnRef || colTypes?.[colIndex];
      const w = key ? widthMap.get(key) : undefined;
      const needsWidth = w !== undefined;
      const needsResize = isFirstDataRow && onColumnResize && key;

      if (!needsWidth && !needsResize) {
        return cell;
      }

      return {
        ...cell,
        style: {
          ...(cell.style ?? {}),
          ...(needsWidth ? { width: w, minWidth: w, maxWidth: w } : {}),
          ...(needsResize ? { position: 'relative' as const } : {}),
        },
        ...(needsResize
          ? {
            resizeHandle: (
              <ColumnResizeHandle
                columnType={key}
                colLegend={cell.colLegend ?? cell.columnLegend ?? ''}
                onResize={onColumnResize}
              />
            ),
          }
          : {}),
      };
    });
  });
}

export function applyColumnReorderToGrid(
  gridData: any[] | null | undefined,
  colTypes: string[] | undefined,
  onColumnReorder: (sourceColumn: string, targetColumn: string) => void
): any[] | null | undefined {
  if (!gridData) {
    return gridData;
  }

  return gridData.map((row, rowIndex) => {
    // Row 0 is the legend row (may be hidden when alwaysDisplayLegend=false).
    // Row 1 is the first data/header row — the same row targeted by applyResizeToGrid.
    if (rowIndex !== 1) {
      return row;
    }

    return row.map((cell: any, colIndex: number) => {
      const key = cell.columnRef || colTypes?.[colIndex];
      if (!key) {
        return cell;
      }

      return {
        ...cell,
        style: {
          ...(cell.style ?? {}),
          position: 'relative' as const,
        },
        dragHandle: <ColumnDragHandle columnType={key} />,
      };
    });
  });
}

export function extractCellLabel(cell: any): string | null {
  const val = cell?.value;
  if (val === null || val === undefined) return null;
  if (typeof val === 'string') return val || null;
  if (typeof val === 'number') return String(val);
  // React element — try props.title (SortIndicator) or props.children
  if (typeof val === 'object' && 'props' in val) {
    if (typeof val.props?.title === 'string' && val.props.title) return val.props.title;
    if (typeof val.props?.children === 'string') return val.props.children;
  }
  return null;
}

export function columnTypeToLabel(columnType: string): string {
  return columnType
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}
